import { useEffect, useRef, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ShoppingCart, Info, Loader2, Wallet, Zap, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { marketApi, purchaseApi, authApi, balanceApi } from '@/lib/api';
import { reachGoal, GOAL_PURCHASE_CLICK, GOAL_PURCHASE_ERROR } from '@/lib/metrika';
import { TradeUrlRequiredDialog } from '@/components/TradeUrlRequiredDialog';
import { OrderStatusModal } from '@/components/OrderStatusModal';
import { ItemAttributes } from '@/components/ItemAttributes';
import type { StickerLike } from '@/components/ItemAttributes';

interface Listing {
  id: number;
  product_id: string;
  full_name: string;
  price: number;
  currency: string;
  wear: string;
  float_value: number;
  paint_seed: number;
  is_stattrak: boolean;
  is_souvenir: boolean;
  has_stickers: boolean;
  stickers: StickerLike[];
  image: string;
  weapon: string;
  skin: string;
  canonical_name: string;
  // Из расширенного прайса — те же поля, что на карточке товара.
  paint_index?: number | null;
  phase?: string | null;
  keychains?: StickerLike[];
  nametag?: string | null;
  slow_delivery?: boolean;
}

interface Card {
  slug: string;
  title: string;
  image: string;
  // Нужны блоку характеристик — тип и редкость показываются рядом с float.
  category?: string;
  rarity?: string;
  rarity_color?: string;
}

export function ListingPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [listing, setListing] = useState<Listing | null>(null);
  const [card, setCard] = useState<Card | null>(null);
  const [loading, setLoading] = useState(true);

  const [showTradeUrlDialog, setShowTradeUrlDialog] = useState(false);
  const [buying, setBuying] = useState(false);
  const [orderStatusTxId, setOrderStatusTxId] = useState<number | null>(null);
  const [pendingPayment, setPendingPayment] = useState<{ url: string; formData: Record<string, string> } | null>(null);
  const [balance, setBalance] = useState<number | null>(null);
  const paymentPopupRef = useRef<Window | null>(null);

  useEffect(() => {
    loadListing();
    if (authApi.isAuthenticated()) {
      balanceApi.getBalance()
        .then((d) => setBalance((d as { balance: number }).balance))
        .catch(() => setBalance(null));
    }
    // Восстанавливаем txId если мобильный браузер ушёл на страницу оплаты и вернулся
    const savedTx = sessionStorage.getItem('xpanda_pending_tx');
    if (savedTx) {
      setOrderStatusTxId(Number(savedTx));
      sessionStorage.removeItem('xpanda_pending_tx');
    }
  }, [id]);

  const loadListing = async () => {
    try {
      setLoading(true);
      const data = await marketApi.getListingDetail(Number(id));
      setListing(data.listing);
      setCard(data.card);

      // SEO
      document.title = `${data.listing.full_name} — купить | CS2 MARKET`;
    } catch (error: any) {
      toast.error('Ошибка загрузки: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBuyClick = () => {
    if (buying) return;  // защита от двойного клика
    if (!authApi.isAuthenticated()) {
      toast.error('Необходимо войти через Steam для покупки');
      navigate('/login');
      return;
    }
    reachGoal(GOAL_PURCHASE_CLICK, { listing_id: listing?.id });
    handleConfirmBuy();
  };

  const handleConfirmBuy = async (paymentMethod: 'invizo' | 'balance' = 'invizo') => {
    if (!listing) return;

    setBuying(true);
    try {
      const result = await purchaseApi.createPurchase(listing.id, paymentMethod, '', '', false);

      // Balance flow: backend синхронно reserve→Xpanda→confirm, status='completed'.
      if (paymentMethod === 'balance') {
        if (result.status === 'completed') {
          toast.success('Куплено за баланс! Скин будет отправлен.');
          if (result.transaction_id) setOrderStatusTxId(result.transaction_id);
          balanceApi.getBalance()
            .then((d) => setBalance((d as { balance: number }).balance))
            .catch(() => {});
        } else {
          toast.error('Не удалось купить за баланс');
        }
        return;
      }

      // PC возвращает status='pending' для invizo-purchase (PurchaseResponsePending
       // schema). Принимаем оба варианта (pending_payment — legacy от pre-cutout кода).
       if (result.status === 'pending' || result.status === 'pending_payment') {
        if (result.form_data && result.payment_url) {
          // Открываем модал статуса сразу
          if (result.transaction_id) {
            setOrderStatusTxId(result.transaction_id);
            // На мобильных браузер может уйти на страницу оплаты — сохраняем txId
            sessionStorage.setItem('xpanda_pending_tx', String(result.transaction_id));
          }
          // Сохраняем данные для повторного открытия из OrderStatusModal
          setPendingPayment({ url: result.payment_url, formData: result.form_data });
          // Сохраняем в localStorage — кнопка «Открыть страницу оплаты» работает из истории профиля
          if (result.transaction_id) {
            try { localStorage.setItem(`xpanda_payment_${result.transaction_id}`, JSON.stringify({ url: result.payment_url, formData: result.form_data })); } catch {}
          }

          const isMobile = /Mobi|Android/i.test(navigator.userAgent);
          if (isMobile) {
            // На мобильных не открываем popup — пользователь нажмёт «Оплатить» в модале
            toast.success('Нажмите «Оплатить» в окне статуса для перехода к оплате');
          } else {
            toast.success('Перенаправляем на оплату...');
            try {
              // Открываем popup ДО submit — синхронно в click-handler, браузер не блокирует.
              // Если popup заблокирован (null) — fallback к новой вкладке.
              const popup = window.open(
                '',
                'xpanda-payment',
                'width=560,height=700,scrollbars=yes,resizable=yes',
              );
              paymentPopupRef.current = popup;

              const form = document.createElement('form');
              form.method = 'POST';
              form.action = result.payment_url;
              form.target = popup ? 'xpanda-payment' : '_blank';
              form.enctype = 'application/x-www-form-urlencoded';
              form.style.display = 'none';

              Object.entries(result.form_data).forEach(([key, value]) => {
                const input = document.createElement('input');
                input.type = 'hidden';
                input.name = key;
                input.value = String(value);
                form.appendChild(input);
              });

              document.body.appendChild(form);
              form.submit();
              document.body.removeChild(form);

              if (!popup) {
                toast('Оплата открыта в новой вкладке — разрешите всплывающие окна для лучшего опыта', { duration: 5000 });
              }
            } catch (err) {
              console.error('[PAYMENT] Error creating/submitting form:', err);
              toast.error('Ошибка при создании формы оплаты');
            }
          }

          return;
        }

        reachGoal(GOAL_PURCHASE_ERROR, { listing_id: listing.id, reason: 'no_form_data' });
        toast.error('Ошибка создания платежа: нет данных формы');
      } else {
        reachGoal(GOAL_PURCHASE_ERROR, { listing_id: listing.id, reason: 'bad_status' });
        toast.error('Ошибка создания платежа');
      }
    } catch (error: any) {
      if (error.message?.includes('Steam trade URL') || error.message?.includes('trade_url')) {
        setShowTradeUrlDialog(true);
      } else {
        reachGoal(GOAL_PURCHASE_ERROR, { listing_id: listing.id, reason: error.message });
        toast.error('Ошибка покупки: ' + error.message);
      }
    } finally {
      setBuying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0d1117] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#ff6b00]"></div>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="min-h-screen bg-[#0d1117] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-2">Предмет не найден</h1>
          <Link to="/" className="text-[#ff6b00] hover:underline">На главную</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0d1117]">
      {/* Breadcrumbs */}
      <div className="bg-[#161b22] border-b border-[#30363d]">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-3 max-w-[1920px] mx-auto">
          <nav className="flex items-center gap-2 text-sm">
            <Link to="/" className="text-[#8b949e] hover:text-white">Главная</Link>
            <span className="text-[#8b949e]">/</span>
            <Link to={`/market/${card?.slug}`} className="text-[#8b949e] hover:text-white">
              {card?.title || listing.canonical_name}
            </Link>
            <span className="text-[#8b949e]">/</span>
            <span className="text-white">{listing.wear || 'Предмет'}</span>
          </nav>
        </div>
      </div>

      <div className="w-full px-4 sm:px-6 lg:px-8 py-8 max-w-[1920px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Image */}
          <div className="bg-[#161b22] rounded-2xl p-8 border border-[#30363d] flex items-center justify-center">
            {listing.image ? (
              <img
                src={listing.image}
                alt={listing.full_name}
                className="max-w-full max-h-[750px] object-contain drop-shadow-2xl"
              />
            ) : (
              <div className="w-64 h-64 bg-[#0d1117] rounded-lg flex items-center justify-center">
                <span className="text-[#8b949e]">Нет изображения</span>
              </div>
            )}
          </div>

          {/* Info */}
          <div className="space-y-6">
            {/* Title */}
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
                {listing.full_name}
              </h1>
              {listing.is_stattrak && (
                <Badge className="bg-[#ff6b00]/20 text-[#ff6b00] border-[#ff6b00]">
                  StatTrak™
                </Badge>
              )}
              {listing.is_souvenir && (
                <Badge className="bg-yellow-500/20 text-yellow-500 border-yellow-500 ml-2">
                  Souvenir
                </Badge>
              )}
            </div>

            {/* Price Card */}
            <div className="bg-[#161b22] rounded-xl p-6 border border-[#30363d]">
              <div className="flex items-baseline gap-2 mb-4">
                <span className="text-5xl font-bold text-[#ff6b00]">
                  {Math.round(listing.price).toLocaleString('ru-RU')} ₽
                </span>
              </div>

              <Button
                size="lg"
                className="w-full bg-[#ff6b00] hover:bg-[#ff8533] text-white h-14 text-lg"
                onClick={handleBuyClick}
                disabled={buying}
              >
                {buying ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Создание платежа...
                  </>
                ) : (
                  <>
                    <ShoppingCart className="w-5 h-5 mr-2" />
                    Купить картой за {Math.round(listing.price).toLocaleString('ru-RU')} ₽
                  </>
                )}
              </Button>

              {/* Купить за баланс — только если хватает */}
              {balance !== null && balance >= Math.round(listing.price) && (
                <Button
                  size="lg"
                  variant="outline"
                  className="w-full mt-2 border-[#30363d] text-white hover:bg-[#161b22] h-14 text-lg"
                  onClick={() => {
                    if (!authApi.isAuthenticated()) { navigate('/login'); return; }
                    handleConfirmBuy('balance');
                  }}
                  disabled={buying}
                >
                  <Wallet className="w-5 h-5 mr-2 text-[#ff6b00]" />
                  Купить за баланс ({balance.toLocaleString('ru-RU')} ₽)
                </Button>
              )}

              {/* Страница пер-офферная, поэтому обещание доставки обязано
                  соответствовать именно этому экземпляру. */}
              <p className="text-[#8b949e] text-sm mt-4 text-center flex items-center justify-center gap-2">
                {listing.slow_delivery ? (
                  <>
                    <Clock className="w-4 h-4 text-amber-400 flex-shrink-0" />
                    Доставка до 12 часов через Steam Trade Offer
                  </>
                ) : (
                  <>
                    <Zap className="w-4 h-4 text-green-400 flex-shrink-0" />
                    Мгновенная доставка через Steam Trade Offer
                  </>
                )}
              </p>
            </div>

            {/* Характеристики — тот же компонент, что на карточке товара.
                Прежде здесь был свой блок: без шкалы float, без редкости,
                фазы, брелков и именного ярлыка, а наклейки читались из
                sticker.image, хотя поле называется image_url — поэтому они
                тут не показывались вовсе. */}
            <ItemAttributes
              floatValue={listing.float_value}
              paintSeed={listing.paint_seed}
              phase={listing.phase}
              category={card?.category}
              rarity={card?.rarity}
              rarityColor={card?.rarity_color}
              wear={listing.wear}
              nametag={listing.nametag}
              stickers={listing.stickers}
              keychains={listing.keychains}
            />

            {(listing.float_value === null || listing.float_value === undefined) &&
             (!listing.stickers || listing.stickers.length === 0) && (
              <div className="flex items-start gap-2 p-3 bg-[#0d1117] rounded-lg border border-[#30363d]">
                <Info className="w-4 h-4 text-[#8b949e] flex-shrink-0 mt-0.5" />
                <p className="text-[#8b949e] text-xs">
                  Данные о float/наклейках недоступны для данного предложения
                </p>
              </div>
            )}

            {/* Back link */}
            <Link
              to={`/market/${card?.slug}`}
              className="inline-flex items-center gap-2 text-[#8b949e] hover:text-[#ff6b00] transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Все предложения {card?.title || listing.canonical_name}</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Trade URL Required Dialog */}
      <TradeUrlRequiredDialog
        open={showTradeUrlDialog}
        onClose={() => setShowTradeUrlDialog(false)}
        onSaved={() => {
          setShowTradeUrlDialog(false);
          handleConfirmBuy();
        }}
      />

      <OrderStatusModal
        transactionId={orderStatusTxId}
        paymentUrl={pendingPayment?.url}
        paymentFormData={pendingPayment?.formData}
        onClose={() => { setOrderStatusTxId(null); setPendingPayment(null); sessionStorage.removeItem('xpanda_pending_tx'); }}
        onPaymentConfirmed={() => {
          paymentPopupRef.current?.close();
          paymentPopupRef.current = null;
        }}
      />
    </div>
  );
}
